import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown } from 'lucide-react';
import HoverBar from './HoverBar';
import SplitNote from './SplitNote';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MetricCard({ label, value, valueColor }) {
  return (
    <div className="rounded-lg border bg-card p-4 text-center" data-testid={`growth-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${valueColor || ''}`}>{value}</p>
    </div>
  );
}

function BarChart({ data, isDark }) {
  if (!data || data.length === 0) return null;

  const maxAmount = Math.max(...data.map(d => d.amount));
  // Round up to a nice ceiling
  const rawCeiling = maxAmount * 1.2; // 20% headroom
  let ceiling;
  if (rawCeiling <= 0.5) ceiling = 0.5;
  else if (rawCeiling <= 1) ceiling = 1;
  else if (rawCeiling <= 2) ceiling = 2;
  else if (rawCeiling <= 5) ceiling = 5;
  else ceiling = Math.ceil(rawCeiling / 2) * 2;
  
  // Y-axis labels
  const yLabels = [];
  const steps = 4;
  for (let i = steps; i >= 0; i--) {
    const val = ceiling * i / steps;
    yLabels.push(`$${val % 1 === 0 ? val : val.toFixed(2)}`);
  }

  // Determine if last bar is higher than previous
  const lastIdx = data.length - 1;
  const prevAmount = lastIdx > 0 ? data[lastIdx - 1].amount : 0;
  const lastIsHigher = data[lastIdx].amount >= prevAmount;

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="dividend-bar-chart">
      <h3 className="text-sm font-bold mb-4">Annual Dividend Per Share — Last {data.length} Years</h3>
      <div className="flex items-end gap-0">
        {/* Y-axis */}
        <div className="flex flex-col justify-between h-48 pr-2 shrink-0">
          {yLabels.map((label, i) => (
            <span key={i} className="text-[10px] font-mono text-muted-foreground leading-none">{label}</span>
          ))}
        </div>
        {/* Bars */}
        <div className="flex-1 flex items-end gap-2 sm:gap-4 h-48 border-b border-l border-border/40 pl-1 pb-0">
          {data.map((d, i) => {
            const heightPct = ceiling > 0 ? (d.amount / ceiling) * 100 : 0;
            const isLast = i === lastIdx;

            // Bar fill color. Last (most recent) bar gets a green/red
            // tint based on YoY direction; older bars use a neutral
            // gray that works in both themes. Using Tailwind's `dark:`
            // variant (CSS-driven) instead of the `isDark` prop keeps
            // the bars rendering even if the prop ever drifts out of
            // sync with the actual <html class="dark"> state — which
            // was the May 2026 bug where bars went white-on-white in
            // light mode.
            let barColor;
            // Round P145 (Feb 2026) — Cyan color for the year the
            // cadence switched (SATA's 2026 bar). Visually pairs with
            // the cyan cadence-switch banner above the chart so
            // users instantly connect the two.
            if (d.cadence_switched_on) {
              barColor = 'bg-cyan-500';
            } else if (isLast) {
              barColor = lastIsHigher ? 'bg-green-500' : 'bg-red-500';
            } else {
              barColor = 'bg-gray-700 dark:bg-white/90';
            }

            // Year-over-year growth vs the immediately prior bar. Only
            // appended when we actually have the previous amount > 0 —
            // otherwise the base label stays clean (no "NaN" / "—").
            const prev = i > 0 ? data[i - 1] : null;
            const base = `${d.year}: $${d.amount.toFixed(2)}`;
            let label = base;
            if (prev && prev.amount > 0) {
              const pct = ((d.amount - prev.amount) / prev.amount) * 100;
              const up = pct >= 0;
              const arrow = up ? '▲' : '▼';
              const sign = up ? '+' : '';
              const color = up ? 'text-emerald-400' : 'text-rose-400';
              label = (
                <>
                  {base}{' '}
                  <span className={color}>
                    ({arrow} {sign}{pct.toFixed(1)}% vs {prev.year})
                  </span>
                </>
              );
            }

            return (
              <div key={d.year} className="flex-1 flex flex-col items-center justify-end h-full">
                <HoverBar
                  label={label}
                  className={`w-full max-w-[60px] rounded-t-sm transition-all duration-500 ${barColor}`}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                  testId={`bar-${d.year}`}
                  isDark={isDark}
                />
                <span className="text-[10px] font-mono text-muted-foreground mt-2">{d.year}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function DivGrowthTab({ ticker, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/dividend-growth`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => setError('Could not load dividend growth data'))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading growth data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[200px]" data-testid="div-growth-error">
        <div className="text-center text-muted-foreground">
          <TrendingDown className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error || 'No dividend growth data available'}</p>
        </div>
      </div>
    );
  }

  const growthColor = data.growth_5yr >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  const cagrColor = data.cagr_5yr >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

  // Round P145 (Feb 2026) — Cadence-switch suppression. When the
  // stock changed dividend cadence mid-history (SATA went Monthly →
  // Daily on June 15/16 2026), the year-over-year comparison is
  // apples-to-oranges: 2025's partial-year total sits next to
  // 2026's full-year daily projection, blowing the CAGR up to a
  // nonsense +1,173% headline. Hide the growth + CAGR tiles behind
  // an explainer so users don't act on the fake number. The bar
  // chart and current-annual tile stay visible.
  const cadenceSwitched = !!data.cadence_switch_date;
  // Also detect the "we only have 1 year of gap" degenerate case —
  // growth == CAGR to 1 decimal place means the formula collapsed
  // (n_years=1) and both metrics are the same headline number.
  const cagrDegenerate = (
    data.growth_5yr != null
    && data.cagr_5yr != null
    && Math.abs(Number(data.growth_5yr) - Number(data.cagr_5yr)) < 0.1
    && (Array.isArray(data.annual_data) ? data.annual_data.length : 0) < 3
  );
  const hideCagr = cadenceSwitched || cagrDegenerate;
  const _formatSwitchMonth = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(`${iso}T12:00:00Z`);
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="div-growth-tab">
      {/* Round P145 — Cadence-switch banner. Explains why the
          "5YR Growth" and "5YR CAGR" tiles are missing when a
          daily payer (SATA) is opened. Only renders when the API
          reported a cadence flip. */}
      {cadenceSwitched && (
        <div
          className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-800 dark:text-cyan-200"
          data-testid="div-growth-cadence-switch-banner"
        >
          <span className="font-semibold">Growth math suppressed.</span>{' '}
          This ticker switched from <span className="font-mono">{data.cadence_before_switch || 'monthly'}</span> to <span className="font-mono font-bold">{data.cadence_after_switch || 'daily'}</span> payouts in <span className="font-mono">{_formatSwitchMonth(data.cadence_switch_date)}</span>, so the year-over-year comparison mixes a partial-cadence year with an annualized daily rate — the CAGR would read as fake growth. The bar chart below still tells the real story.
        </div>
      )}
      {/* Metric cards */}
      <div className={`grid gap-3 ${hideCagr ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-3'}`}>
        {!hideCagr && (
          <MetricCard
            label="5YR Growth"
            value={`${data.growth_5yr >= 0 ? '+' : ''}${data.growth_5yr}%`}
            valueColor={growthColor}
          />
        )}
        {!hideCagr && (
          <MetricCard
            label="5YR CAGR"
            value={`${data.cagr_5yr >= 0 ? '+' : ''}${data.cagr_5yr}%`}
            valueColor={cagrColor}
          />
        )}
        <MetricCard
          label="Current Annual"
          value={`$${data.current_annual?.toFixed(2) || '0.00'}`}
        />
      </div>

      {/* Oct 2026 — split note: amounts below are split-adjusted, so a
          chart that looks flat across a split is NOT a cut. */}
      <SplitNote splits={data.splits} testId="div-growth-split-note" />

      {/* Bar Chart */}
      <BarChart data={data.annual_data} isDark={isDark} />

      {/* Growth Summary */}
      {data.summary && (
        <div className="rounded-lg border bg-card p-4" data-testid="growth-summary">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            Growth Summary
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{
            __html: data.summary
              .replace(/\$[\d.]+\/share/g, '<strong class="text-foreground">$&</strong>')
          }} />
        </div>
      )}

      {/* Data source footer — Round 95y (Jun 2026) admin-only.
          Originally surfaced to all users so they knew whether Yahoo
          or Massive provenance backed the bars. Per spec, end-users
          shouldn't be exposed to source attribution; only the admin
          (you) needs to see this for debugging fallback behaviour.
          Now also surfaces "cache (24m)" when the new Round 95y
          fallback supplemented sparse Massive data. */}
      {isAdmin && (
        <p className="text-[10px] text-muted-foreground/70 text-right" data-testid="divgrowth-data-source">
          <span className="font-bold uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80 mr-1">admin:</span>
          source{' '}
          <span className="font-semibold">
            {data.data_source === 'massive' ? 'Massive'
              : data.data_source === 'cache' ? 'cache (24m backfill)'
              : 'Yahoo Finance'}
          </span>
        </p>
      )}
    </div>
  );
}
